// Calendar and wording helpers for the Programme tab. Pure, so they can be
// tested without the programme contexts.

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const mondayOf = (d) => {
  const day = startOfDay(d);
  day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
  return day;
};

// "YYYY-MM-DD" is a local calendar date; new Date() would read it as UTC.
const localDate = (value) => {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * The Monday of week 1 (#92): the programme's start_date, or for one saved
 * before that existed, the Monday of the week it was created.
 */
export const programmeStart = (programme) => {
  if (programme?.start_date) return localDate(programme.start_date);
  const created = programme?.created_at ? localDate(programme.created_at) : null;
  return created ? mondayOf(created) : null;
};

/** Monday of `weekNumber`. Weeks run Monday to Sunday. */
export const weekStartDate = (programme, weekNumber) => {
  const start = programmeStart(programme);
  if (!start) return null;
  const d = new Date(start);
  d.setDate(d.getDate() + (weekNumber - 1) * 7);
  return d;
};

/** The current programme week (1 to the length), from today's date. */
export const currentProgrammeWeek = (programme, now = new Date()) => {
  const start = programmeStart(programme);
  if (!start) return 0;
  // Round: a daylight-saving change makes one day 23 or 25 hours long.
  const days = Math.round((startOfDay(now) - start) / DAY_MS);
  const week = Math.floor(days / 7) + 1;
  return Math.min(Math.max(week, 1), Number(programme.duration_weeks) || 1);
};

// Even spreads of training days, 0 = Monday ... 6 = Sunday (#92).
export const DEFAULT_WEEKDAYS = {
  1: [0],
  2: [0, 3],
  3: [0, 2, 4],
  4: [0, 1, 3, 5],
  5: [0, 1, 2, 4, 5],
  6: [0, 1, 2, 3, 4, 5],
  7: [0, 1, 2, 3, 4, 5, 6],
};

export const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** The training days, chosen or the default spread; null above 7 a week. */
export const weekdaysFor = (programmeOrCount, chosen) => {
  const perWeek = typeof programmeOrCount === "number" ? programmeOrCount : Number(programmeOrCount?.days_per_week);
  const picked = chosen ?? (typeof programmeOrCount === "object" ? programmeOrCount?.weekdays : null);
  if (Array.isArray(picked) && picked.length === perWeek) return [...picked].map(Number).sort((a, b) => a - b);
  return DEFAULT_WEEKDAYS[perWeek] || null;
};

/** "Mon · Wed · Fri". */
export const describeWeekdays = (days) => (days || []).map((d) => SHORT[d]).join(" · ");

/**
 * The weekday (0 Mon to 6 Sun) a slot is on: the day it was moved to in the
 * week pool (#196), else its planned day. Null in the pool, or when days
 * aren't mapped (doubles).
 */
export const slotWeekday = (programme, slot) => {
  if (slot?.unplaced) return null;
  if (slot?.planned_weekday != null) return Number(slot.planned_weekday);
  const days = weekdaysFor(programme);
  return days?.[slot?.day_index] ?? null;
};

/** The date of a programme day, or null in the pool or when days aren't mapped. */
export const slotDate = (programme, slot) => {
  const weekday = slotWeekday(programme, slot);
  const monday = weekStartDate(programme, slot?.week_number);
  if (weekday == null || !monday) return null;
  const d = new Date(monday);
  d.setDate(d.getDate() + weekday);
  return d;
};

/** The date of a weekday in a programme week. */
export const weekDayDate = (programme, weekNumber, weekday) => {
  const monday = weekStartDate(programme, weekNumber);
  if (!monday) return null;
  const d = new Date(monday);
  d.setDate(d.getDate() + weekday);
  return d;
};

/** "YYYY-MM-DD" for a local calendar date. */
export const toIsoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** A "YYYY-MM-DD" (or Date) as a local date at midnight; null when unreadable. */
export const parseLocalDate = (value) => {
  const d = value ? localDate(value) : null;
  return d ? startOfDay(d) : null;
};

/** 0 = Monday ... 6 = Sunday. */
export const weekdayOf = (d) => (d.getDay() + 6) % 7;

/**
 * Week 1's day indexes before the first day (#125). The API leaves them out
 * of the programme, so they aren't shown or missed. Empty when the programme
 * starts on its Monday or days double up.
 */
export const daysBeforeStart = (programme) => {
  const first = parseLocalDate(programme?.first_day);
  const monday = programmeStart(programme);
  if (!first || !monday) return [];
  const offset = Math.round((first - monday) / DAY_MS);
  if (!(offset > 0 && offset < 7)) return [];
  const days = weekdaysFor(programme) || [];
  return days.flatMap((day, index) => (day < offset ? [index] : []));
};

/** Week 1 is short: the programme started after its Monday (#125). */
export const isShortFirstWeek = (programme) => daysBeforeStart(programme).length > 0;

/**
 * The start choices for a programme beginning around `today` (#125).
 * `days` are the training days (null above 7 a week).
 * - leftThisWeek: training days from today to Sunday (null when not mapped)
 * - todayUseful: starting today schedules at least one day this week
 * - suggested: "today", or "monday" when less than half the week is left
 *   or nothing is left to train this week
 */
export const startChoices = (today, days) => {
  const t = startOfDay(today);
  const dow = weekdayOf(t);
  const leftThisWeek = days ? days.filter((d) => d >= dow).length : null;
  const todayUseful = leftThisWeek === null || leftThisWeek > 0;
  const nextMonday = new Date(t);
  nextMonday.setDate(t.getDate() + 7 - dow);
  return {
    today: t,
    nextMonday,
    isMonday: dow === 0,
    leftThisWeek,
    todayUseful,
    suggested: !todayUseful || 7 - dow < 3.5 ? "monday" : "today",
  };
};

/**
 * The first day for a start choice: { mode: "today" | "monday" | "date",
 * date } where `date` is "YYYY-MM-DD" for a picked day. A mode that doesn't
 * apply (today with nothing left this week) falls back to next Monday.
 */
export const resolveFirstDay = (choice, today, days) => {
  const opts = startChoices(today, days);
  const mode = choice?.mode || opts.suggested;
  if (mode === "date") return parseLocalDate(choice.date) || opts.today;
  return mode === "today" && opts.todayUseful ? opts.today : opts.nextMonday;
};

/** Training days in week 1 from `firstDay` on: how full the first week is. */
export const firstWeekCount = (firstDay, days, perWeek) =>
  days ? days.filter((d) => d >= weekdayOf(firstDay)).length : perWeek;

/** "Wed 8 Oct". */
export const formatSlotDay = (date) =>
  date ? `${SHORT[(date.getDay() + 6) % 7]} ${date.getDate()} ${date.toLocaleDateString("en-AU", { month: "short" })}` : null;

/** "30 Sep – 6 Oct", or "7 – 13 Oct" inside one month. */
export const formatWeekRange = (programme, weekNumber) => {
  const from = weekStartDate(programme, weekNumber);
  if (!from) return null;
  const to = new Date(from);
  to.setDate(to.getDate() + 6);
  const month = (d) => d.toLocaleDateString("en-AU", { month: "short" });
  return from.getMonth() === to.getMonth()
    ? `${from.getDate()} – ${to.getDate()} ${month(to)}`
    : `${from.getDate()} ${month(from)} – ${to.getDate()} ${month(to)}`;
};

/** Where a slot sits in its week: { index (1-based), count }. */
export const slotPosition = (programme, slot) => {
  const week = (programme?.weeks || []).find(
    (w) => w.week_number === slot?.week_number,
  );
  const sessions = week?.sessions || [];
  const index = sessions.findIndex((s) => s.id === slot?.id) + 1;
  return { index, count: sessions.length };
};

/**
 * The slot's identity ("Upper · Strength"). A built session's own name is
 * secondary: the slot is what the programme planned, and it should survive
 * the session being built and done.
 */
export const slotName = (slot) =>
  slot?.scheduled_label || slot?.label || "Session";

/** A built session's own name, when it differs from the slot name. */
export const builtName = (slot) => {
  // A done slot is named for what was done (#192), which may have been
  // changed before or during the run.
  const done = slot?.status === "completed" ? slot.session_plan_data?.label : null;
  if (!done && !slot?.plan_data) return null;
  const name = done || slot.label || slot.plan_data.label;
  return name && name !== slotName(slot) ? name : null;
};

/** Older programmes stored focus lowercased ("strength"); show it as a label. */
export const displayFocus = (focus) => {
  if (!focus) return "";
  return focus === focus.toLowerCase()
    ? focus.replace(/\b\p{L}/gu, (c) => c.toUpperCase())
    : focus;
};

/** "Thu 1 Oct" style date for a completed session. */
export const formatSessionDate = (iso, { weekday = false } = {}) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-AU", {
    ...(weekday ? { weekday: "short" } : {}),
    day: "numeric",
    month: "short",
  });
};

/** Done / missed / total counts across the whole programme. */
export const programmeCounts = (programme) => {
  const all = (programme?.weeks || []).flatMap((w) => w.sessions || []);
  return {
    total: all.length,
    completed: all.filter((s) => s.status === "completed").length,
    missed: all.filter((s) => s.status === "missed").length,
    skipped: all.filter((s) => s.status === "skipped").length,
  };
};
